import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SignatureCanvas from "react-signature-canvas";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Check,
  FileSignature,
  Image as ImageIcon,
  PenLine,
  ShieldCheck,
  Type,
  X,
  ChevronDown,
  FileText,
  UserPlus,
  BookmarkCheck,
  RotateCcw,
  CheckCircle2,
  Plus,
  Edit3,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const steps = [
  "Upload",
  "Configure Digital Identity",
  "Choose Mode",
  "Advanced Options",
  "Customize Signature",
  "Review & Sign",
];

const stampColors = ["#4f46e5", "#2563eb", "#10b981", "#ef4444", "#6b7280", "#111827"];

const signatureTypes = [
  { id: "trust-stamp", title: "Quantum Trust Stamp", subtitle: "Recommended", icon: ShieldCheck },
  { id: "ink-stamp", title: "Ink Signature", subtitle: "Stamp + ink signature", icon: PenLine },
  { id: "stamp-signature", title: "Stamp + Ink Signature", subtitle: "Combined appearance", icon: FileSignature },
  { id: "typed-stamp", title: "Typed Name", subtitle: "Type and apply stamp", icon: Type },
  { id: "image-stamp", title: "Custom Image", subtitle: "Upload your signature image", icon: ImageIcon },
];

const categoryTabs = [
  "All Templates",
  "Business",
  "Government",
  "Banking",
  "Healthcare",
  "Engineering",
  "IT / DevOps",
  "AI / Data",
  "Others",
];

const templateCards = [
  { id: 1, title: "Standard Digital Signature", subtitle: "General business, contracts, HR documents", category: "Business" },
  { id: 2, title: "Enterprise Approval Signature", subtitle: "Purchase orders, ERP, internal approvals", category: "Business" },
  { id: 3, title: "Legal Signature", subtitle: "Legal contracts, agreements, case docs", category: "Business" },
  { id: 4, title: "Government Signature", subtitle: "e-Governance, official documents", category: "Government" },
  { id: 5, title: "Banking Signature", subtitle: "Banking, financial approvals", category: "Banking" },
  { id: 6, title: "Healthcare Signature", subtitle: "Medical documents, prescriptions", category: "Healthcare" },
  { id: 7, title: "Manufacturing Quality Approval", subtitle: "Quality checks, inspections, audit reports", category: "Engineering" },
  { id: 8, title: "Engineering Drawing Approval", subtitle: "Engineering drawings, design approvals", category: "Engineering" },
  { id: 9, title: "Executive Approval", subtitle: "Executive, board, management approvals", category: "Business" },
  { id: 10, title: "Invisible Signature", subtitle: "Invoices, automated workflows, ERP", category: "Others" },
  { id: 11, title: "Handwritten Signature", subtitle: "Handwritten look with digital signature", category: "Others" },
  { id: 12, title: "Handwritten + Corporate Seal", subtitle: "Traditional signature with company seal", category: "Others" },
  { id: 13, title: "QR Verification Signature", subtitle: "Public verification with QR code", category: "IT / DevOps" },
  { id: 14, title: "Blockchain Verified Signature", subtitle: "Blockchain anchored, tamper-proof proof", category: "IT / DevOps" },
  { id: 15, title: "Post-Quantum Trust Signature", subtitle: "I-PQSign Exclusive - Quantum Safe Assurance", category: "IT / DevOps" },
  { id: 16, title: "Firmware / Software Signing", subtitle: "Software, firmware, containers, binaries", category: "IT / DevOps" },
  { id: 17, title: "Source Code Commit Signature", subtitle: "Code commits, repositories", category: "IT / DevOps" },
  { id: 18, title: "AI Model Signature", subtitle: "AI models, datasets, ML artifacts", category: "AI / Data" },
  { id: 19, title: "Document Timestamp Only", subtitle: "Timestamp without visible signature", category: "Others" },
  { id: 20, title: "Digital Seal Only", subtitle: "Organization seal, authentication", category: "Others" },
  { id: 21, title: "Multi-Signer Signature", subtitle: "Multi-party, parallel approvals", category: "Business" },
  { id: 22, title: "Wet Ink + Digital Signature", subtitle: "Scanned ink signature + digital", category: "Others" },
  { id: 23, title: "Notarized Signature", subtitle: "Notarized documents", category: "Government" },
  { id: 24, title: "Time-Locked Signature", subtitle: "Embargoed, time-release documents", category: "Others" },
];

const stampPositions = Array.from({ length: 9 }, (_, index) => index);

function isPDF(filename) {
  return filename?.toLowerCase().endsWith(".pdf");
}

function isImage(filename) {
  return /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(filename || "");
}

function getPreviewName(file) {
  return file?.name || file?.file?.name || file?.originalFile?.name || "";
}

function typedSignatureToImage(name, color, font = "Dancing Script") {
  const canvas = document.createElement("canvas");
  canvas.width = 700;
  canvas.height = 220;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = color;
  ctx.font = `64px "${font}", cursive`;
  ctx.textBaseline = "middle";
  ctx.fillText(name || "Your Name", 40, 110);
  return canvas.toDataURL("image/png");
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function readImageAsDataUrl(file, callback) {
  const reader = new FileReader();
  reader.onload = () => callback(reader.result);
  reader.readAsDataURL(file);
}

const SANS_FONT = "-apple-system, BlinkMacSystemFont, sans-serif";
const CID_TEXT = "QmX8a7b...9kJ2L";

function traceCardPath(ctx, x, y, w, h, radius) {
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, radius);
  } else {
    ctx.rect(x, y, w, h);
  }
}

function drawSeal(ctx, sealImg, { cardW, cardH, size, margin }) {
  if (!sealImg) return;
  ctx.drawImage(sealImg, cardW - size - margin, cardH - size - margin, size, size);
}

function drawVerifiedBadge(ctx) {
  const badgeX = 230;
  const badgeY = 20;
  ctx.strokeStyle = "#86efac";
  ctx.fillStyle = "#f0fdf4";
  traceCardPath(ctx, badgeX, badgeY, 90, 26, 13);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#16a34a";
  ctx.font = `bold 12px ${SANS_FONT}`;
  ctx.textBaseline = "middle";
  ctx.fillText("✓  Verified", badgeX + 14, badgeY + 13);
}

function drawDetailRow(ctx, label, value, y) {
  ctx.font = `bold 11px ${SANS_FONT}`;
  ctx.fillStyle = "#1e293b";
  ctx.fillText(label, 20, y);

  const labelWidth = ctx.measureText(label).width;
  ctx.font = `normal 11px ${SANS_FONT}`;
  ctx.fillStyle = "#475569";
  ctx.fillText(value, 20 + labelWidth + 6, y);
}

function drawClassicTemplate(ctx, { cardW, cardH, sealImg, visibility, signerName, signerOrganization, algorithm, certificateIssuer, formattedTimestamp }) {
  ctx.font = "italic bold 26px Georgia, serif";
  ctx.fillStyle = "#1e40af";
  ctx.textBaseline = "top";
  if (visibility.showSignerDetails) {
    ctx.fillText(signerName, 20, 20);
  }
  if (visibility.showTrustScore) {
    drawVerifiedBadge(ctx);
  }

  ctx.strokeStyle = "#e2e8f0";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(20, 58);
  ctx.lineTo(cardW - 20, 58);
  ctx.stroke();

  ctx.textBaseline = "top";
  const rows = [
    [visibility.showSignerDetails, "Digitally Signed by:", signerName],
    [visibility.showTimestamp, "Date:", formattedTimestamp],
    [visibility.showOrganization, "Org:", signerOrganization],
    [visibility.showAlgorithm, "Algorithm:", algorithm],
    [visibility.showCertificate, "Issuer:", certificateIssuer],
    [visibility.showCid, "CID (IPFS):", CID_TEXT],
  ].filter(([visible]) => visible);

  rows.forEach(([, label, value], index) => {
    drawDetailRow(ctx, label, value, 70 + index * 24);
  });

  drawSeal(ctx, sealImg, { cardW, cardH, size: 54, margin: 20 });
}

function drawGenericTemplate(ctx, { templateId, cardW, cardH, inkImg, sealImg, visibility, signerName, signerOrganization, algorithm, certificateIssuer, formattedTimestamp, fontFamily, penColor, stampColor }) {
  const activeTemplate = templateCards.find((t) => t.id === templateId);
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillStyle = stampColor;
  ctx.font = "bold 13px sans-serif";
  ctx.fillText(activeTemplate ? activeTemplate.title.toUpperCase() : "DIGITALLY SIGNED", 20, 20);

  if (inkImg) {
    const scaleToFit = Math.min(180 / inkImg.width, 46 / inkImg.height);
    ctx.drawImage(inkImg, 20, 42, inkImg.width * scaleToFit, inkImg.height * scaleToFit);
  } else if (visibility.showSignerDetails) {
    ctx.fillStyle = penColor || "#0f172a";
    ctx.font = `italic bold 22px "${fontFamily}", cursive`;
    ctx.fillText(signerName, 20, 44);
  }

  ctx.fillStyle = "#334155";
  ctx.font = "11px sans-serif";

  const lines = [
    [visibility.showSignerDetails, `Signer: ${signerName}`],
    [visibility.showOrganization, `Org: ${signerOrganization}`],
    [visibility.showAlgorithm, `Algorithm: ${algorithm}`],
    [visibility.showCertificate, `Issuer: ${certificateIssuer}`],
    [visibility.showTimestamp, `Date: ${formattedTimestamp}`],
    [visibility.showCid, `CID: ${CID_TEXT}`],
  ].filter(([visible]) => visible);

  lines.forEach(([, text], index) => {
    ctx.fillText(text, 20, 96 + index * 20);
  });

  drawSeal(ctx, sealImg, { cardW, cardH, size: 50, margin: 16 });
}

async function createSignatureStampImage({
  templateId = 1,
  algorithm,
  certificateIssuer,
  formattedTimestamp,
  fontFamily = "Dancing Script",
  penColor = "#111827",
  signatureImage,
  sealImage,
  signerName = "Demo",
  signerOrganization = "Intrust Innovation Labs",
  stampColor = "#4f46e5",
  stampShape = "rounded",
  elementsVisibility = {},
}) {
  const visibility = {
    showSignerDetails: true,
    showOrganization: true,
    showAlgorithm: true,
    showCertificate: true,
    showTimestamp: true,
    showTrustScore: true,
    showCid: true,
  };
  Object.entries(elementsVisibility).forEach(([key, value]) => {
    if (value !== undefined) visibility[key] = value;
  });

  const inkImg = await loadImage(signatureImage);
  const sealImg = await loadImage(sealImage);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const scale = 2;
  const cardW = 340;
  const cardH = 220;
  canvas.width = cardW * scale;
  canvas.height = cardH * scale;
  ctx.scale(scale, scale);

  ctx.fillStyle = "#ffffff";
  traceCardPath(ctx, 0, 0, cardW, cardH, stampShape === "rounded" ? 14 : 4);
  ctx.fill();

  ctx.strokeStyle = stampColor || "#cbd5e1";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const drawTemplate = templateId === 1 ? drawClassicTemplate : drawGenericTemplate;
  drawTemplate(ctx, {
    templateId,
    cardW,
    cardH,
    inkImg,
    sealImg,
    visibility,
    signerName,
    signerOrganization,
    algorithm,
    certificateIssuer,
    formattedTimestamp,
    fontFamily,
    penColor,
    stampColor,
  });

  return canvas.toDataURL("image/png");
}

function Card({ title, titleIcon: TitleIcon, children, className = "", bodyClassName = "px-4 pb-4 pt-2" }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        {TitleIcon && <TitleIcon size={15} className="text-indigo-600" />}
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
      </div>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

function renderCardGraphic(id, signerName, options = {}) {
  const {
    algorithm = "Dilithium3",
    certificateIssuer = "Intrust Quantum CA",
    formattedTimestamp = "21-Jun-2026 10:35 UTC",
    organization = "Intrust Innovation Labs",
    sealImage = "",
    elementsVisibility = {},
  } = options;

  const {
    showSignerDetails = true,
    showOrganization = true,
    showAlgorithm = true,
    showCertificate = true,
    showTimestamp = true,
    showTrustScore = true,
    showCid = true,
  } = elementsVisibility;

  switch (id) {
    case 1:
      return (
        <div className="p-2 bg-white rounded border border-slate-200 text-[9px] leading-tight space-y-1 relative">
          <div className="flex items-center justify-between border-b pb-1">
            {showSignerDetails ? (
              <span className="font-serif italic text-blue-800 text-xs font-bold">{signerName}</span>
            ) : (
              <span className="font-bold text-slate-700 text-[10px]">DIGITALLY SIGNED</span>
            )}
            {showTrustScore && (
              <div className="flex items-center gap-0.5 text-[7.5px] text-green-600 font-semibold border border-green-300 rounded px-1 py-0.2">
                <CheckCircle2 size={8} /> Verified
              </div>
            )}
          </div>
          <div className="text-[7.5px] text-slate-600 space-y-0.5">
            {showSignerDetails && (
              <p><span className="font-semibold text-slate-800">Digitally Signed by:</span> {signerName}</p>
            )}
            {showTimestamp && (
              <p><span className="font-semibold text-slate-800">Date:</span> {formattedTimestamp}</p>
            )}
            {showOrganization && (
              <p><span className="font-semibold text-slate-800">Org:</span> {organization}</p>
            )}
            {showAlgorithm && (
              <p><span className="font-semibold text-slate-800">Algorithm:</span> {algorithm}</p>
            )}
            {showCertificate && (
              <p><span className="font-semibold text-slate-800">Issuer:</span> {certificateIssuer}</p>
            )}
            {showCid && (
              <p><span className="font-semibold text-slate-800">CID (IPFS):</span> QmX8a7b...9kJ2L</p>
            )}
          </div>
          {sealImage ? (
            <img
              src={sealImage}
              alt="Seal"
              className="absolute bottom-2 right-2 w-8 h-8 rounded-full border border-indigo-200 object-cover pointer-events-none opacity-90"
            />
          ) : null}
        </div>
      );
    case 2:
      return (
        <div className="p-2 bg-white rounded border border-slate-200 text-[8.5px] leading-tight text-center space-y-1 relative">
          {showTrustScore && <p className="font-bold text-green-600 text-[10px] tracking-wider">APPROVED</p>}
          {showSignerDetails && <p className="font-bold text-slate-800">{signerName}</p>}
          {showOrganization && <p className="text-[7.5px] text-slate-500 font-medium">{organization}</p>}
          <div className="border border-green-200 bg-green-50/50 rounded p-1 text-[7.5px] text-left text-slate-600 space-y-0.5">
            {showTimestamp && (
              <p className="flex items-center gap-1"><CheckCircle2 size={8} className="text-green-600" /> Approved On: {formattedTimestamp}</p>
            )}
            {showCertificate && <p className="truncate">Cert: {certificateIssuer}</p>}
            {showAlgorithm && <p>Algorithm: {algorithm}</p>}
          </div>
          {sealImage ? (
            <img
              src={sealImage}
              alt="Seal"
              className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full border border-indigo-200 object-cover pointer-events-none opacity-90"
            />
          ) : null}
        </div>
      );
    default:
      return (
        <div className="p-2 bg-white rounded border border-slate-200 text-[8.5px] leading-tight space-y-1 relative">
          <div className="flex items-center justify-between border-b pb-1">
            <span className="font-bold text-indigo-700 text-[9px]">VERIFIED DOCUMENT</span>
            {showTrustScore && (
              <div className="flex items-center gap-0.5 text-[7px] text-emerald-600 font-bold">
                <CheckCircle2 size={8} /> Verified
              </div>
            )}
          </div>
          <div className="text-[7.5px] text-slate-600 space-y-0.5">
            {showSignerDetails && <p><span className="font-semibold">Signer:</span> {signerName}</p>}
            {showOrganization && <p><span className="font-semibold">Org:</span> {organization}</p>}
            {showAlgorithm && <p><span className="font-semibold">Algorithm:</span> {algorithm}</p>}
            {showCertificate && <p><span className="font-semibold">Certificate:</span> {certificateIssuer}</p>}
            {showTimestamp && <p><span className="font-semibold">Date:</span> {formattedTimestamp}</p>}
            {showCid && <p><span className="font-semibold">CID:</span> QmX8a7b...9kJ2L</p>}
          </div>
          {sealImage ? (
            <img
              src={sealImage}
              alt="Seal"
              className="absolute bottom-2 right-2 w-8 h-8 rounded-full border border-indigo-200 object-cover pointer-events-none opacity-90"
            />
          ) : null}
        </div>
      );
  }
}

const OPTION_DEFAULTS = {
  signatureType: "trust-stamp",
  signatureMode: "draw",
  stampMode: "stamp-signature",
  stampColor: "#4f46e5",
  stampShape: "rounded",
  penColor: "#111827",
  penThickness: "medium",
  fontFamily: "Dancing Script",
};

const NULLISH_DEFAULTS = { stampPosition: 8, stampMargin: 20 };

const VISIBILITY_DEFAULTS = {
  showSignerDetails: true,
  showOrganization: true,
  showAlgorithm: true,
  showCertificate: true,
  showTimestamp: true,
  showTrustScore: true,
  showCid: true,
};

const DEFAULT_SIGNER_NAME = "Dr. Noorul Hussain";

function withFalsyDefaults(saved, defaults) {
  const source = saved || {};
  return Object.fromEntries(
    Object.entries(defaults).map(([key, fallback]) => [key, source[key] || fallback])
  );
}

function withNullishDefaults(saved, defaults) {
  const source = saved || {};
  return Object.fromEntries(
    Object.entries(defaults).map(([key, fallback]) => [key, source[key] ?? fallback])
  );
}

function resolveInitialState(state, user) {
  const saved = state.signatureOptions;
  return {
    selectedTemplateId: state.selectedTemplateId || 1,
    templateCustomData: state.templateCustomData || {},
    corporateSealImage: state.sealImage || "",
    signatureImage: state.signatureImage || "",
    typedName: saved?.typedName || user?.name || DEFAULT_SIGNER_NAME,
    options: {
      ...withFalsyDefaults(saved, OPTION_DEFAULTS),
      ...withNullishDefaults(saved, NULLISH_DEFAULTS),
    },
    visibility: withNullishDefaults(saved?.elementsVisibility, VISIBILITY_DEFAULTS),
  };
}

function resolveSigner(state, user, selectedDigitalId, typedName) {
  return {
    signerName: typedName || user?.name || DEFAULT_SIGNER_NAME,
    signerTitle: state.identity?.title || user?.title || "CEO & Founder",
    signerOrganization:
      state.identity?.organization || user?.organization || "Intrust Innovation Labs Pvt. Ltd.",
    algorithm:
      selectedDigitalId?.algorithm || state.identity?.algorithm || state.algorithm || "Dilithium3",
    certificateIssuer: selectedDigitalId?.issuer || state.identity?.issuer || "Intrust Quantum CA",
  };
}

function resolvePreviewFile(state) {
  const previewFile = state.selectedFile || state.uploadedFiles?.[0] || state.uploadedFile || null;
  const actualPreviewFile = previewFile?.file || previewFile?.originalFile || previewFile;
  return { previewFile, actualPreviewFile, previewName: getPreviewName(previewFile) };
}

function getPreviewUrl(previewFile, objectUrl) {
  return previewFile?.url || previewFile?.preview || objectUrl;
}

function formatTimestamp(date) {
  const formatted = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formatted} IST`;
}

function getTemplateTitle(templateId) {
  return templateCards.find((t) => t.id === templateId)?.title || "Select Template";
}

function getVisibleTemplates(category) {
  return category === "All Templates"
    ? templateCards
    : templateCards.filter((card) => card.category === category);
}

function getDrawnSignature(canvas) {
  return canvas && !canvas.isEmpty() ? canvas.toDataURL("image/png") : null;
}

function resolveBaseSignature({ signatureType, signatureImage, name, penColor, fontFamily }) {
  return signatureType === "typed-stamp"
    ? typedSignatureToImage(name, penColor, fontFamily)
    : signatureImage;
}

const createUploadHandler = (onLoaded) => (event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  readImageAsDataUrl(file, onLoaded);
};

function getStampPositionStyle(position, marginPx) {
  const margin = `${marginPx}px`;
  const positions = [
    { top: margin, left: margin },
    { top: margin, left: "50%", transform: "translateX(-50%)" },
    { top: margin, right: margin },
    { top: "50%", left: margin, transform: "translateY(-50%)" },
    { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
    { top: "50%", right: margin, transform: "translateY(-50%)" },
    { bottom: margin, left: margin },
    { bottom: margin, left: "50%", transform: "translateX(-50%)" },
    { bottom: margin, right: margin },
  ];
  return positions[position] ?? positions[8];
}

function useElementWidth(ref) {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect.width) {
          setWidth(Math.floor(entry.contentRect.width));
        }
      });
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

function usePreviewObjectUrl(previewFile, actualPreviewFile) {
  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    if (actualPreviewFile instanceof File && !previewFile?.url && !previewFile?.preview) {
      const nextUrl = URL.createObjectURL(actualPreviewFile);
      setObjectUrl(nextUrl);
      return () => URL.revokeObjectURL(nextUrl);
    }
    setObjectUrl("");
    return undefined;
  }, [actualPreviewFile, previewFile?.preview, previewFile?.url]);

  return objectUrl;
}

function useCurrentTime() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timerId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timerId);
  }, []);

  return now;
}

const STEP_TONES = {
  done: { circle: "bg-green-500 border-green-500 text-white", label: "text-green-600" },
  current: { circle: "bg-indigo-600 border-indigo-600 text-white", label: "text-indigo-600" },
  upcoming: { circle: "bg-white border-gray-300 text-gray-500", label: "text-gray-500" },
};

function getStepTone(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

function Stepper({ steps: stepList, currentStep }) {
  return (
    <div className="flex items-center justify-between mb-4 h-[72px] py-2">
      {stepList.map((step, index) => {
        const tone = getStepTone(index, currentStep);
        const isDone = tone === "done";
        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== stepList.length - 1 && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  isDone ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_TONES[tone].circle}`}
            >
              {isDone ? <Check size={16} strokeWidth={3} className="text-white" /> : index + 1}
            </div>
            <p
              className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_TONES[tone].label}`}
            >
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function SignatureBlock({ stampShape, stampColor, templateId, signerName, graphicOptions }) {
  const shapeClass = stampShape === "rounded" ? "rounded-xl" : "rounded-md";
  return (
    <div
      className={`overflow-hidden border bg-white shadow-md max-w-[280px] ${shapeClass}`}
      style={{ borderColor: stampColor }}
    >
      <div className="p-0.5">{renderCardGraphic(templateId, signerName, graphicOptions)}</div>
    </div>
  );
}

function PreviewPlaceholder({ title, subtitle }) {
  return (
    <div className="grid min-h-[560px] place-items-center rounded-xl border border-dashed border-slate-300 bg-white">
      <div className="text-center">
        <FileText className="mx-auto mb-3 text-slate-400" />
        <p className="text-sm font-semibold text-slate-700">{title}</p>
        <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

function PdfPreview({ previewUrl, containerRef, containerWidth, activePreviewMode, stampStyle, signatureBlock }) {
  const [numPages, setNumPages] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const pageCount = numPages || 1;
  const pageWidth = containerWidth || 540;

  const stamp = (
    <div className="absolute z-20" style={stampStyle}>
      {signatureBlock}
    </div>
  );

  const singlePage = (
    <div className="relative w-full bg-white">
      <Document
        file={previewUrl}
        onLoadSuccess={({ numPages: total }) => {
          setNumPages(total);
          setPreviewPage(1);
        }}
        loading={
          <div className="grid min-h-[560px] w-full place-items-center bg-white text-sm text-slate-500">
            Loading document preview...
          </div>
        }
      >
        <Page
          pageNumber={previewPage}
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
      {stamp}
    </div>
  );

  const allPages = (
    <div className="w-full space-y-4">
      {Array.from({ length: pageCount }, (_, idx) => idx + 1).map((pageNumber) => (
        <div key={pageNumber} className="relative w-full bg-white shadow-sm">
          <Document file={previewUrl}>
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          {pageNumber === 1 && stamp}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col w-full">
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden bg-white flex flex-col items-center justify-center"
      >
        {activePreviewMode === "single" ? singlePage : allPages}
      </div>

      <div className="mt-3 flex items-center justify-center gap-3 text-xs font-semibold text-slate-600 border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={() => setPreviewPage((page) => Math.max(1, page - 1))}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 shadow-xs disabled:opacity-50"
          disabled={previewPage <= 1}
        >
          Prev
        </button>
        <span>
          {previewPage} / {pageCount}
        </span>
        <button
          type="button"
          onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 shadow-xs disabled:opacity-50"
          disabled={previewPage >= pageCount}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function DocumentPreview({ previewUrl, previewName, containerRef, containerWidth, activePreviewMode, stampStyle, signatureBlock }) {
  if (!previewUrl) {
    return (
      <PreviewPlaceholder
        title="No document selected"
        subtitle="Upload a PDF or image to preview the signature placement."
      />
    );
  }

  if (isPDF(previewName)) {
    return (
      <PdfPreview
        previewUrl={previewUrl}
        containerRef={containerRef}
        containerWidth={containerWidth}
        activePreviewMode={activePreviewMode}
        stampStyle={stampStyle}
        signatureBlock={signatureBlock}
      />
    );
  }

  if (isImage(previewName)) {
    return (
      <div className="relative w-full overflow-hidden bg-white">
        <img
          src={previewUrl}
          alt={previewName || "Uploaded document"}
          className="w-full h-auto object-contain"
        />
        <div className="absolute z-20" style={stampStyle}>
          {signatureBlock}
        </div>
      </div>
    );
  }

  return <PreviewPlaceholder title="Unsupported file type" subtitle={previewName} />;
}

const PREVIEW_MODES = [
  ["single", "Single Page View"],
  ["all", "All Pages View"],
];

function PreviewModeToggle({ mode, onChange }) {
  return (
    <div className="flex gap-2">
      {PREVIEW_MODES.map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${
            mode === value
              ? "border-indigo-600 bg-indigo-50 text-indigo-700"
              : "border-slate-200 text-slate-600"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function TemplateSelectionModal({
  onClose,
  onEdit,
  onSelect,
  cards,
  activeCategory,
  setActiveCategory,
  selectedTemplateId,
  signerName,
  graphicOptions,
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4"
      onClick={() => onClose()}
    >
      <div
        className="flex w-full max-w-[1400px] max-h-[92vh] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {categoryTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveCategory(tab)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  activeCategory === tab
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg bg-indigo-700 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-800"
            >
              <Plus size={14} /> Custom Template
            </button>
            <button
              type="button"
              onClick={() => onClose()}
              className="p-1.5 hover:bg-slate-100 rounded-full"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {cards.map((card) => {
              const isSelected = selectedTemplateId === card.id;
              return (
                <div
                  key={card.id}
                  className={`relative flex flex-col justify-between rounded-xl border-2 p-3 text-left shadow-sm transition ${
                    isSelected
                      ? "border-emerald-600 bg-emerald-50/40 ring-2 ring-emerald-500 ring-offset-1"
                      : "border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md"
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-2.5 right-2 z-10 flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-0.5 text-[9px] font-extrabold text-white shadow">
                      <Check size={10} strokeWidth={3} /> Selected
                    </div>
                  )}

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                            isSelected ? "bg-emerald-600" : "bg-slate-600"
                          }`}
                        >
                          {card.id}
                        </span>
                        <span
                          className={`text-xs font-bold leading-tight ${
                            isSelected ? "text-emerald-950" : "text-slate-900"
                          }`}
                        >
                          {card.title}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`mb-2.5 rounded-lg border bg-white p-1.5 shadow-2xs ${
                        isSelected ? "border-emerald-300" : "border-slate-200"
                      }`}
                    >
                      {renderCardGraphic(card.id, signerName || "Your Name", graphicOptions)}
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-1.5 border-t border-slate-100 pt-2">
                    <button
                      type="button"
                      onClick={() => onEdit(card)}
                      className="flex items-center justify-center gap-1 flex-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      <Edit3 size={11} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onSelect(card.id)}
                      className={`flex-1 rounded px-2 py-1 text-[11px] font-bold transition text-center ${
                        isSelected
                          ? "bg-emerald-600 text-white cursor-default"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                    >
                      {isSelected ? "Selected" : "Select"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {cards.length === 0 && (
            <p className="py-10 text-center text-sm text-slate-500">
              No templates in this category yet.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function EditTemplateModal({
  editingTemplate,
  onClose,
  onApply,
  typedName,
  setTypedName,
  modalSigRef,
  penColor,
  handleUpload,
  handleSealUpload,
  corporateSealImage,
  templateCustomData,
  setTemplateCustomData,
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      onClick={() => onClose()}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-5 shadow-2xl border border-slate-200 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b pb-2">
          <div>
            <span className="text-[11px] font-bold text-indigo-600 uppercase">
              Template #{editingTemplate.id}
            </span>
            <h3 className="text-sm font-bold text-slate-900">{editingTemplate.title}</h3>
          </div>
          <button type="button" onClick={() => onClose()}>
            <X size={16} className="text-slate-400 hover:text-slate-600" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              Signer Name
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
            />
          </div>

          {[11, 12, 22].includes(editingTemplate.id) && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
              <label className="block text-xs font-bold text-slate-700">
                Handwritten Ink Signature
              </label>
              <div className="rounded border border-slate-200 bg-white p-1">
                <SignatureCanvas
                  ref={modalSigRef}
                  penColor={penColor}
                  canvasProps={{ className: "w-full h-24 rounded" }}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => modalSigRef.current?.clear()}
                  className="text-[11px] font-semibold text-rose-600 hover:underline"
                >
                  Clear Canvas
                </button>
                <label className="cursor-pointer text-[11px] font-semibold text-indigo-600 hover:underline">
                  Upload Signature Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                  />
                </label>
              </div>
            </div>
          )}

          {[12, 20].includes(editingTemplate.id) && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
              <label className="block text-xs font-bold text-slate-700">
                Corporate Seal / Stamp
              </label>
              <div className="flex items-center gap-3">
                {corporateSealImage ? (
                  <img
                    src={corporateSealImage}
                    alt="Seal Preview"
                    className="h-12 w-12 rounded-full border border-blue-800 object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-[8px] text-slate-400 text-center font-bold">
                    No Seal
                  </div>
                )}
                <div className="flex-1">
                  <label className="inline-block cursor-pointer rounded border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    Upload Seal Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleSealUpload}
                    />
                  </label>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-slate-600">
                  Seal Text (Fallback)
                </label>
                <input
                  type="text"
                  placeholder="e.g. INTRUST LABS CHENNAI"
                  value={templateCustomData.sealText || ""}
                  onChange={(e) =>
                    setTemplateCustomData({
                      ...templateCustomData,
                      sealText: e.target.value,
                    })
                  }
                  className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          )}

          {[3, 4, 5, 23].includes(editingTemplate.id) && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                {editingTemplate.id === 3 && "Case Reference Number"}
                {editingTemplate.id === 4 && "Government Officer ID / Dept"}
                {editingTemplate.id === 5 && "Transaction ID (Tx ID)"}
                {editingTemplate.id === 23 && "Notary License Number"}
              </label>
              <input
                type="text"
                value={templateCustomData.referenceId || ""}
                onChange={(e) =>
                  setTemplateCustomData({
                    ...templateCustomData,
                    referenceId: e.target.value,
                  })
                }
                placeholder="e.g. CIVIL/2026/00125"
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
              />
            </div>
          )}

          {[7, 8, 16, 17, 18].includes(editingTemplate.id) && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Project / Product Name
                </label>
                <input
                  type="text"
                  value={
                    templateCustomData.project || templateCustomData.product || ""
                  }
                  onChange={(e) =>
                    setTemplateCustomData({
                      ...templateCustomData,
                      project: e.target.value,
                      product: e.target.value,
                    })
                  }
                  placeholder="e.g. Bridge Const."
                  className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Batch / Drawing / Revision
                </label>
                <input
                  type="text"
                  value={
                    templateCustomData.referenceId ||
                    templateCustomData.revision ||
                    ""
                  }
                  onChange={(e) =>
                    setTemplateCustomData({
                      ...templateCustomData,
                      referenceId: e.target.value,
                      revision: e.target.value,
                    })
                  }
                  placeholder="e.g. DRW-2026-778"
                  className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          )}

          {editingTemplate.id === 24 && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">
                Time-Locked Until Date
              </label>
              <input
                type="date"
                value={templateCustomData.referenceId || ""}
                onChange={(e) =>
                  setTemplateCustomData({
                    ...templateCustomData,
                    referenceId: e.target.value,
                  })
                }
                className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button
            type="button"
            onClick={() => onClose()}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            Apply & Select Template
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CustomizationCard({
  stampColor,
  setStampColor,
  stampShape,
  setStampShape,
  stampPosition,
  setStampPosition,
  stampMargin,
  setStampMargin,
}) {
  return (
    <Card title="2. Customization" bodyClassName="px-4 pb-4 pt-2">
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-xs font-semibold text-slate-600">Theme</p>
          <div className="flex gap-3">
            {stampColors.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setStampColor(color)}
                className="h-6 w-6 rounded-full border-2 border-white shadow ring-1 ring-slate-200"
                style={{
                  backgroundColor: color,
                  outline: stampColor === color ? "3px solid #4f46e5" : "none",
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-slate-600">Stamp Shape</p>
          <div className="grid grid-cols-2 gap-2">
            {["rounded", "rectangular"].map((shape) => (
              <button
                key={shape}
                type="button"
                onClick={() => setStampShape(shape)}
                className={`rounded-md border px-3 py-2 text-xs font-semibold capitalize ${
                  stampShape === shape
                    ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                    : "border-slate-200 text-slate-600"
                }`}
              >
                {shape}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-slate-600">Stamp Position</p>
          <div className="grid grid-cols-3 gap-2">
            {stampPositions.map((position) => (
              <button
                key={position}
                type="button"
                onClick={() => setStampPosition(position)}
                className={`grid h-9 place-items-center rounded-md border ${
                  stampPosition === position
                    ? "border-indigo-600 bg-indigo-50"
                    : "border-slate-200"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor:
                      stampPosition === position ? stampColor : "#cbd5e1",
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-slate-600">Stamp Margin</p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="60"
              value={stampMargin}
              onChange={(e) => setStampMargin(Number(e.target.value))}
              className="w-full accent-indigo-600"
            />
            <span className="w-12 text-right text-xs font-semibold text-slate-700">
              {stampMargin}px
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function StampElementsCard({ items }) {
  return (
    <Card title="5. Stamp Elements" bodyClassName="p-3">
      <div className="space-y-3 text-sm">
        {items.map(([label, checked, setChecked]) => (
          <label
            key={label}
            className="flex cursor-pointer items-center justify-between gap-3"
          >
            <span className="text-xs font-medium text-slate-700">{label}</span>
            <div
              onClick={() => setChecked(!checked)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                checked ? "bg-indigo-600" : "bg-slate-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  checked ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </div>
          </label>
        ))}
      </div>
    </Card>
  );
}

export default function CustomizeSignature() {
  const navigate = useNavigate();
  const location = useLocation();
  const sigRef = useRef(null);
  const modalSigRef = useRef(null);
  const previewContainerRef = useRef(null);
  const containerWidth = useElementWidth(previewContainerRef);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const state = location.state || {};
  const selectedDigitalId = state.selectedDigitalId || null;
  const initial = resolveInitialState(state, user);

  // Retrieve Visual Seal configuration from advanced.js
  const visualSealEnabled = Boolean(state.visualSealEnabled);

  const [selectedTemplateId, setSelectedTemplateId] = useState(initial.selectedTemplateId);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateCustomData, setTemplateCustomData] = useState(initial.templateCustomData);
  const [corporateSealImage, setCorporateSealImage] = useState(initial.corporateSealImage);

  const { signatureType, signatureMode, stampMode, penColor, penThickness, fontFamily } =
    initial.options;
  const [stampColor, setStampColor] = useState(initial.options.stampColor);
  const [stampShape, setStampShape] = useState(initial.options.stampShape);
  const [stampPosition, setStampPosition] = useState(initial.options.stampPosition);
  const [stampMargin, setStampMargin] = useState(initial.options.stampMargin);
  const [typedName, setTypedName] = useState(initial.typedName);
  const [signatureImage, setSignatureImage] = useState(initial.signatureImage);

  const [showSignerDetails, setShowSignerDetails] = useState(initial.visibility.showSignerDetails);
  const [showOrganization, setShowOrganization] = useState(initial.visibility.showOrganization);
  const [showAlgorithm, setShowAlgorithm] = useState(initial.visibility.showAlgorithm);
  const [showCertificate, setShowCertificate] = useState(initial.visibility.showCertificate);
  const [showTimestamp, setShowTimestamp] = useState(initial.visibility.showTimestamp);
  const [showTrustScore, setShowTrustScore] = useState(initial.visibility.showTrustScore);
  const [showCid, setShowCid] = useState(initial.visibility.showCid);

  const [activePreviewMode, setActivePreviewMode] = useState("single");
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All Templates");
  const currentStep = 4;

  const { previewFile, actualPreviewFile, previewName } = resolvePreviewFile(state);
  const objectUrl = usePreviewObjectUrl(previewFile, actualPreviewFile);
  const previewUrl = getPreviewUrl(previewFile, objectUrl);
  const currentTimestamp = useCurrentTime();

  // Visual seal image to apply in stamp/canvas and preview
  const activeSealImage = visualSealEnabled ? corporateSealImage : "";
  const visibleTemplateCards = getVisibleTemplates(activeCategory);

  const elementsVisibility = {
    showSignerDetails,
    showOrganization,
    showAlgorithm,
    showCertificate,
    showTimestamp,
    showTrustScore,
    showCid,
  };

  const elementItems = [
    ["Show Signer Name", showSignerDetails, setShowSignerDetails],
    ["Show Organization", showOrganization, setShowOrganization],
    ["Show Algorithm", showAlgorithm, setShowAlgorithm],
    ["Show Certificate", showCertificate, setShowCertificate],
    ["Show Timestamp", showTimestamp, setShowTimestamp],
    ["Show Trust Score", showTrustScore, setShowTrustScore],
    ["Show CID (IPFS)", showCid, setShowCid],
  ];

  const { signerName, signerTitle, signerOrganization, algorithm, certificateIssuer } =
    resolveSigner(state, user, selectedDigitalId, typedName);
  const formattedTimestamp = formatTimestamp(currentTimestamp);

  const graphicOptions = {
    algorithm,
    certificateIssuer,
    formattedTimestamp,
    organization: signerOrganization,
    sealImage: activeSealImage,
    elementsVisibility,
  };

  const saveTypedSignature = () => {
    setSignatureImage(typedSignatureToImage(signerName, penColor, fontFamily));
  };

  const handleUpload = createUploadHandler(setSignatureImage);
  const handleSealUpload = createUploadHandler(setCorporateSealImage);

  const handleSelectTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    setShowStyleModal(false);
  };

  const handleSaveEditedTemplate = () => {
    if (!editingTemplate) return;
    const drawnSignature = getDrawnSignature(modalSigRef.current);
    if (drawnSignature) {
      setSignatureImage(drawnSignature);
    }
    handleSelectTemplate(editingTemplate.id);
    setEditingTemplate(null);
  };

  const handleSave = async () => {
    const baseSignatureImage = resolveBaseSignature({
      signatureType,
      signatureImage,
      name: signerName,
      penColor,
      fontFamily,
    });

    const finalSignatureImage = await createSignatureStampImage({
      templateId: selectedTemplateId,
      algorithm,
      certificateIssuer,
      formattedTimestamp,
      fontFamily,
      penColor,
      signatureImage: baseSignatureImage,
      sealImage: activeSealImage,
      signerName,
      signerOrganization,
      stampColor,
      stampShape,
      templateCustomData,
      elementsVisibility,
    });

    navigate("/reviewsign", {
      state: {
        ...state,
        identity: state.identity,
        selectedDigitalId,
        uploadedFile: state.uploadedFile,
        documentData: state.documentData,
        uploadedFiles: state.uploadedFiles,
        algorithm,
        signingMode: state.signingMode,
        timestampEnabled: state.timestampEnabled,
        timestampAuthority: state.timestampAuthority,
        timestampHashAlgo: state.timestampHashAlgo,
        blockchainEnabled: state.blockchainEnabled,
        blockchainType: state.blockchainType,
        blockchainNetwork: state.blockchainNetwork,
        documentEncryptionEnabled: state.documentEncryptionEnabled,
        encryptionAlgorithm: state.encryptionAlgorithm,
        encryptionPassword: state.encryptionPassword,
        certificateEmbeddingEnabled: state.certificateEmbeddingEnabled,
        certificateChainOption: state.certificateChainOption,
        revocationCheckEnabled: state.revocationCheckEnabled,
        revocationMethod: state.revocationMethod,
        revocationFallback: state.revocationFallback,
        signaturePolicyEnabled: state.signaturePolicyEnabled,
        policyType: state.policyType,
        documentLockEnabled: state.documentLockEnabled,
        visualSealEnabled,
        signerAttributeEnabled: state.signerAttributeEnabled,
        ipRestrictionEnabled: state.ipRestrictionEnabled,
        signingReasonEnabled: state.signingReasonEnabled,
        documentId: state.documentId,
        selectedTemplateId,
        templateCustomData,
        signatureImage: finalSignatureImage,
        sealImage: activeSealImage,
        selectedFile: previewFile,
        signatureOptions: {
          templateId: selectedTemplateId,
          signatureType,
          signatureMode,
          stampMode,
          stampColor,
          stampShape,
          stampPosition,
          stampMargin,
          penColor,
          penThickness,
          fontFamily,
          typedName,
          elementsVisibility,
          visualSealEnabled,
        },
      },
    });
  };

  const signatureBlock = (
    <SignatureBlock
      stampShape={stampShape}
      stampColor={stampColor}
      templateId={selectedTemplateId}
      signerName={signerName}
      graphicOptions={graphicOptions}
    />
  );

  return (
    <div className="min-h-screen bg-white p-0 text-slate-900">
      {/* Stepper */}
      <Stepper steps={steps} currentStep={currentStep} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)_320px]">
        {/* Left Column: Style Picker & Properties */}
        <aside className="space-y-4">
          <button
            type="button"
            onClick={() => setShowStyleModal(true)}
            className="flex w-full items-center justify-between rounded-xl border-2 border-emerald-600 bg-emerald-50/30 px-4 py-3 shadow-sm hover:border-emerald-700 transition"
          >
            <div className="flex items-center gap-2">
              <BadgeCheck size={26} className="text-emerald-600" />
              <div className="text-left">
                <span className="block text-xs text-emerald-700 font-bold">
                  Selected Signature Style
                </span>
                <span className="text-sm font-extrabold text-slate-900 truncate">
                  {getTemplateTitle(selectedTemplateId)}
                </span>
              </div>
            </div>
            <ChevronDown size={16} className="text-slate-400" />
          </button>

          {/* Template Selection Modal */}
          {showStyleModal && (
            <TemplateSelectionModal
              onClose={() => setShowStyleModal(false)}
              onEdit={setEditingTemplate}
              onSelect={handleSelectTemplate}
              cards={visibleTemplateCards}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              selectedTemplateId={selectedTemplateId}
              signerName={signerName}
              graphicOptions={graphicOptions}
            />
          )}

          {/* Edit Template Modal */}
          {editingTemplate && (
            <EditTemplateModal
              editingTemplate={editingTemplate}
              onClose={() => setEditingTemplate(null)}
              onApply={handleSaveEditedTemplate}
              typedName={typedName}
              setTypedName={setTypedName}
              modalSigRef={modalSigRef}
              penColor={penColor}
              handleUpload={handleUpload}
              handleSealUpload={handleSealUpload}
              corporateSealImage={corporateSealImage}
              templateCustomData={templateCustomData}
              setTemplateCustomData={setTemplateCustomData}
            />
          )}

          {/* Customization Card */}
          <CustomizationCard
            stampColor={stampColor}
            setStampColor={setStampColor}
            stampShape={stampShape}
            setStampShape={setStampShape}
            stampPosition={stampPosition}
            setStampPosition={setStampPosition}
            stampMargin={stampMargin}
            setStampMargin={setStampMargin}
          />
        </aside>

        {/* Center Column: Document Preview */}
        <main className="space-y-4">
          <Card title="3. Signature Preview on Document">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                This shows how your signature will appear on the document.
              </p>
              <PreviewModeToggle mode={activePreviewMode} onChange={setActivePreviewMode} />
            </div>
            <DocumentPreview
              previewUrl={previewUrl}
              previewName={previewName}
              containerRef={previewContainerRef}
              containerWidth={containerWidth}
              activePreviewMode={activePreviewMode}
              stampStyle={getStampPositionStyle(stampPosition, stampMargin)}
              signatureBlock={signatureBlock}
            />
          </Card>
        </main>

        {/* Right Column: Signer Details, Elements & Actions */}
        <aside className="space-y-4">
          <Card title="4. Signature Details" bodyClassName="p-3">
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Signer Name
                </label>
                <input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Title
                </label>
                <input
                  value={signerTitle}
                  readOnly
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Organization
                </label>
                <input
                  value={signerOrganization}
                  readOnly
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
                />
              </div>

              <button
                type="button"
                className="flex items-center gap-1 text-xs font-semibold text-indigo-700"
              >
                More Signer Details <ChevronDown size={14} />
              </button>
            </div>
          </Card>

          <StampElementsCard items={elementItems} />

          <Card title="Quick Actions" bodyClassName="p-3">
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => document.getElementById("signature-upload")?.click()}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2 text-left text-xs font-semibold text-indigo-600 shadow-sm transition hover:bg-slate-50"
              >
                <UserPlus size={16} className="text-indigo-600" /> Upload Signature Image
              </button>

              <button
                type="button"
                onClick={saveTypedSignature}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2 text-left text-xs font-semibold text-indigo-600 shadow-sm transition hover:bg-slate-50"
              >
                <BookmarkCheck size={16} className="text-indigo-600" /> Save as Template
              </button>

              <button
                type="button"
                onClick={() => {
                  setSignatureImage("");
                  setCorporateSealImage("");
                  sigRef.current?.clear?.();
                }}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2 text-left text-xs font-semibold text-indigo-600 shadow-sm transition hover:bg-slate-50"
              >
                <RotateCcw size={18} className="text-indigo-600" /> Reset to Default
              </button>

              <input
                id="signature-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUpload}
              />
            </div>
          </Card>
        </aside>
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => navigate("/advanced", { state })}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
        >
          <ArrowLeft size={16} className="inline mr-1" /> Back
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setSignatureImage("")}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <FileText size={15} className="text-indigo-600" /> Preview Document
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-indigo-700 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-800"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}